import { Data, List, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { reaction } from 'mobx';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { TextConstants } from '../../../Common/TextConstants';
import FileGroupingFileSpecView from '../../FileGroupingFileSpecs/Views/FileGroupingFileSpecView';
import ManageServiceProviderFileGroupingComponentVM from '../Components/ManageServiceProviderFileGroupingComponentVM';
import AddSFTPDetailCommand from '../Models/Commands/AddSFTPBrokerDetailCommand';
import SaveServiceProviderFileGroupingCommand from '../Models/Commands/SaveServiceProviderFileGroupingCommand';
import UpdateSFTPDetailCommand from '../Models/Commands/UpdateSFTPBrokerDetailCommand';
import ServiceProviderFileGroupingLookup from '../Models/Lookups/ServiceProviderFileGroupingLookup';
import ServiceProviderSearchCriteria from '../Models/Queries/ServiceProviderSearchCriteria';
import TradeSftpTypeLookup from '../Models/Lookups/TradeSftpTypeLookup';

@NeoModel
export default class ServiceProviderFileGroupingsVM extends Views.ViewModelBase {
    public tradeSftpTypeOptions = new List(TradeSftpTypeLookup);

    constructor(
        public serviceProviderApiClient = AppService.get(Types.ApiClients.ServiceProviderApiClient),
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private navigation = AppService.get(Types.Neo.Routing.NavigationHelper),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {
        super(taskRunner);
    }

    public serviceProviderSearchCriteria = new ServiceProviderSearchCriteria();
    public manageServiceProviderComponentVM = new ManageServiceProviderFileGroupingComponentVM();

    public pageManager = new Data.PageManager(this.serviceProviderSearchCriteria, ServiceProviderFileGroupingLookup,
        this.serviceProviderApiClient.getPagedServiceProviderDetails, {
        pageSize: 10,
        sortBy: "serviceProviderGroupingName",
        sortAscending: true,
        fetchInitial: true,
        initialTaskRunner: this.taskRunner,
    });

    public async initialise() {
        await this.loadTradeSftpTypes();

        reaction(() => this.serviceProviderSearchCriteria.searchCriteria, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 650 });
    }

    private async loadTradeSftpTypes() {
        try {
            const response = await this.serviceProviderApiClient.getTradeSftpTypes();
            this.tradeSftpTypeOptions.set(response.data);
        } catch (error: any) {
            NotifyUtils.addWarning(TextConstants.Titles.Error, `Error loading trade SFTP types: ${error?.message || error}`, NotificationDuration.Standard);
            this.tradeSftpTypeOptions = new List(TradeSftpTypeLookup);
        }
    }

    public viewFileSpecsForBroker(staticServiceProviderFileGroupingID: number, broker: string) {
        this.navigation.navigateToView(FileGroupingFileSpecView, { fileGroupingID: String(staticServiceProviderFileGroupingID), broker: broker, fileSpecID: String(null), backURL: this.navigation.getCurrentViewPath() });
    }

    public async addServiceProviderFileGrouping() {
        await this.manageServiceProviderComponentVM.initialise();
        this.manageServiceProviderComponentVM.addNewServiceProviderInd = true;
    }

    public cancel() {
        this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping = new ServiceProviderFileGroupingLookup();
        this.manageServiceProviderComponentVM.showManageServiceProviderFileGroupingInd = false;
        this.manageServiceProviderComponentVM.addNewServiceProviderInd = false;
        this.manageServiceProviderComponentVM.isReadOnlyInd = false;
    }

    public async clearSearch() {
        this.serviceProviderSearchCriteria.meta.searchCriteria.value = "";
        await this.pageManager.refreshData();
    }

    public async editServiceProviderFileGrouping(fileGroupingLookup: ServiceProviderFileGroupingLookup) {
        this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping.mapFrom(fileGroupingLookup);
        this.manageServiceProviderComponentVM.isReadOnlyInd = true;
        await this.manageServiceProviderComponentVM.initialise();
    }

    private mapFileGroupingToUpdateCommand() {
        const fileGroupingLookup = this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping;
        const command = new SaveServiceProviderFileGroupingCommand().mapFrom(fileGroupingLookup);

        command.saveFileGroupingExportSettingCommandList = this.manageServiceProviderComponentVM.getSaveExportSettingCommandList();
        command.tradeDistributionTypeId = this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping.tradeDistributionTypeId;

        // Convert to number explicitly
        const samReportingValue = this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping.samReporting;
        command.samReporting = samReportingValue !== null && samReportingValue !== undefined
            ? Number(samReportingValue)
            : null;

        command.sftpBrokerDetailsLookups = this.manageServiceProviderComponentVM.sftpBrokerDetailsList;

        return command;
    }

    public async saveServiceProviderFileGrouping() {
        this.taskRunner.run(async () => {

            if (this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId !== 0) {
                const groupingId = this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId;
                const sftpPersisted = await this.saveServiceProviderFileGroupingSFTPDetails(groupingId);
                if (!sftpPersisted) {
                    return;
                }
            }

            // Create the command once
            const command = this.mapFileGroupingToUpdateCommand();
            const commandJS = command.toJSObject();

            try {
                const response = await this.serviceProviderApiClient.saveServiceProviderFileGrouping(commandJS);

                if (response.data.success) {
                    await this.pageManager.refreshData();
                    NotifyUtils.addSuccess(TextConstants.Titles.Success, "The stockbroker has been successfully saved.", NotificationDuration.Standard);
                }
                else {
                    NotifyUtils.addDanger(TextConstants.Titles.Error, response.data.error, NotificationDuration.Long);
                }
            } catch (error: any) {
                const responseError = error?.response?.data?.error || error?.response?.data?.message;
                const errorMessage = responseError || error?.message || "Failed to save service provider file grouping.";
                NotifyUtils.addDanger(TextConstants.Titles.Error, errorMessage, NotificationDuration.Long);
            }

            this.manageServiceProviderComponentVM.showManageServiceProviderFileGroupingInd = false;
            this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping = new ServiceProviderFileGroupingLookup();
            this.manageServiceProviderComponentVM.addNewServiceProviderInd = false;
            this.manageServiceProviderComponentVM.isReadOnlyInd = false;
        })
    }

    private async saveServiceProviderFileGroupingSFTPDetails(groupingId: number): Promise<boolean> {
        // Delete removed SFTP details first
        const deletedDetails = this.manageServiceProviderComponentVM.deletedSFTPSettingIds;

        for (const deletedDetailId of deletedDetails) {
            if (deletedDetailId > 0) {
                try {
                    const deleteResponse = await this.serviceProviderApiClient.deleteSFTPBrokerDetails(deletedDetailId);
                    if (!deleteResponse.data.success) {
                        NotifyUtils.addDanger(TextConstants.Titles.Error, deleteResponse.data.error || 'Failed to delete SFTP setting', NotificationDuration.Long);
                        return false;
                    }
                } catch (error: any) {
                    NotifyUtils.addDanger(TextConstants.Titles.Error, `Failed to delete SFTP setting: ${error?.message || error}`, NotificationDuration.Long);
                    return false;
                }
            }
        }

        // Then add/update remaining SFTP details
        for (const detail of this.manageServiceProviderComponentVM.sftpBrokerDetailsList) {
            detail.staticServiceProviderFileGroupingId = groupingId;

            if (detail.staticServiceProviderSftpdetailsId > 0) {
                const updateCommand = new UpdateSFTPDetailCommand().mapFrom(detail)?.toJSObject();
                if (!updateCommand) {
                    NotifyUtils.addDanger(TextConstants.Titles.Error, 'Failed to serialize SFTP update command', NotificationDuration.Long);
                    return false;
                }

                const updateResponse = await this.serviceProviderApiClient.setSFTPBrokerDetails(updateCommand);
                if (!updateResponse.data.success) {
                    NotifyUtils.addDanger(TextConstants.Titles.Error, updateResponse.data.error || 'Failed to update SFTP setting', NotificationDuration.Long);
                    return false;
                }
            } else {
                const addCommand = new AddSFTPDetailCommand().mapFrom(detail)?.toJSObject();
                if (!addCommand) {
                    NotifyUtils.addDanger(TextConstants.Titles.Error, 'Failed to serialize SFTP add command', NotificationDuration.Long);
                    return false;
                }

                const addResponse = await this.serviceProviderApiClient.addSFTPBrokerDetails(addCommand);
                if (!addResponse.data.success) {
                    NotifyUtils.addDanger(TextConstants.Titles.Error, addResponse.data.error || 'Failed to add SFTP setting', NotificationDuration.Long);
                    return false;
                }
            }
        }

        return true;
    }
}